/**
 * IMPRESORA — la conexión física.
 *
 * `escpos.ts` construye los bytes del ticket. Este archivo se encarga de
 * entregárselos a la impresora, por dos caminos distintos:
 *
 *   - **USB** (WebUSB): la impresora de mostrador, con su cable.
 *   - **Bluetooth** (Web Bluetooth): las portátiles de reparto y las de bolsillo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DECISIÓN: la impresora se guarda EN EL DISPOSITIVO, no en el negocio
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Podría parecer natural guardar "qué impresora usa este negocio" en la base de
 * datos, junto al resto de los ajustes. Sería un error: un negocio con dos cajas
 * tiene DOS impresoras, y la tablet del dueño no tiene ninguna. Si lo guardáramos
 * en el servidor, configurar la caja 2 desconfiguraría la caja 1.
 *
 * Por eso vive en `localStorage`: cada aparato recuerda la suya. Además el
 * navegador guarda el permiso por su cuenta, así que al abrir Softfi por la
 * mañana la impresora se reconecta sola, sin volver a preguntar nada.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LO QUE HAY QUE SABER ANTES DE PROBAR ESTO EN WINDOWS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * En Windows, cuando instalas el driver de una impresora térmica, Windows la
 * "reserva" para sí mismo (el driver `usbprint.sys` la toma en exclusiva). A
 * partir de ese momento Chrome NO puede hablarle por USB: da un error de
 * "no se pudo tomar la interfaz".
 *
 * No es un fallo de Softfi ni se puede arreglar desde el código: es cómo funciona
 * Windows. Hay dos salidas, y las dos son válidas:
 *
 *   a) **Dejar el driver de Windows y usar la impresión normal** (la de siempre,
 *      con el diálogo). Es lo que Softfi hace de manera automática si no
 *      encuentra impresora ESC/POS. No pierdes nada de lo que ya tenías.
 *
 *   b) **Cambiar el driver a WinUSB** (con una herramienta llamada Zadig) para
 *      que Chrome pueda tomarla. Ganas el corte automático y la impresión sin
 *      diálogo, pero esa impresora deja de aparecer en la lista de impresoras
 *      de Windows: solo imprime desde Softfi.
 *
 * En **Android** no existe este problema: WebUSB y Bluetooth funcionan directo.
 * Y en una tablet Android con una térmica Bluetooth, esto va como un guante.
 */

import { PAGINAS_DE_CODIGOS, type AnchoPapel, type PaginaDeCodigos } from "@/lib/escpos";

// =============================================================================
//  Configuración guardada en este dispositivo
// =============================================================================

export type TipoDeConexion = "usb" | "bluetooth";

export type ConfigImpresora = {
  /** Cómo está conectada. `null` = este dispositivo no tiene impresora ESC/POS. */
  conexion: TipoDeConexion | null;
  /** Para reconocer la impresora USB al reconectar. */
  vendorId?: number;
  productId?: number;
  /** Para reconocer la impresora Bluetooth. */
  bluetoothId?: string;
  /** Nombre legible, solo para mostrarlo en pantalla. */
  nombre?: string;
  ancho: AnchoPapel;
  pagina: PaginaDeCodigos;
  cortarPapel: boolean;
  abrirCajon: boolean;
  /** Imprimir al confirmar la venta, sin que nadie pulse nada. */
  automatica: boolean;
  copias: number;
};

const CLAVE = "softfi.impresora";

export const CONFIG_POR_DEFECTO: ConfigImpresora = {
  conexion: null,
  ancho: "80",
  pagina: "cp850",
  cortarPapel: true,
  abrirCajon: false,
  automatica: false,
  copias: 1,
};

export function leerConfig(): ConfigImpresora {
  try {
    const guardado = localStorage.getItem(CLAVE);
    if (!guardado) return { ...CONFIG_POR_DEFECTO };
    // Mezclamos con los valores por defecto para que una versión vieja guardada
    // sin un campo nuevo no rompa nada.
    return { ...CONFIG_POR_DEFECTO, ...(JSON.parse(guardado) as Partial<ConfigImpresora>) };
  } catch {
    return { ...CONFIG_POR_DEFECTO };
  }
}

export function guardarConfig(config: ConfigImpresora): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(config));
  } catch {
    /* Modo incógnito o almacenamiento lleno: se sigue trabajando sin recordar. */
  }
}

// =============================================================================
//  ¿Puede este navegador?
// =============================================================================

export function soportaUSB(): boolean {
  return typeof navigator !== "undefined" && navigator.usb !== undefined;
}

export function soportaBluetooth(): boolean {
  return typeof navigator !== "undefined" && navigator.bluetooth !== undefined;
}

export function soportaImpresionDirecta(): boolean {
  return soportaUSB() || soportaBluetooth();
}

/** Error con un mensaje pensado para el dueño del negocio, no para un programador. */
export class ErrorDeImpresora extends Error {
  readonly consejo: string;

  constructor(mensaje: string, consejo = "") {
    super(mensaje);
    this.name = "ErrorDeImpresora";
    this.consejo = consejo;
  }
}

// =============================================================================
//  Conexión USB
// =============================================================================

/** La clase 7 del estándar USB es "impresora". Así el selector solo muestra impresoras. */
const CLASE_USB_IMPRESORA = 7;

type CanalUsb = {
  tipo: "usb";
  dispositivo: USBDevice;
  interfaz: number;
  endpoint: number;
};

type CanalBluetooth = {
  tipo: "bluetooth";
  dispositivo: BluetoothDevice;
  caracteristica: BluetoothRemoteGATTCharacteristic;
};

type Canal = CanalUsb | CanalBluetooth;

/**
 * El canal abierto ahora mismo. Vive fuera de React a propósito: la conexión
 * pertenece al aparato, no a una pantalla, y no debe cerrarse porque el usuario
 * navegue de Vender a Inventario.
 */
let canalActivo: Canal | null = null;

/**
 * Busca dentro del dispositivo por dónde se le habla.
 *
 * Un aparato USB puede tener varias "interfaces" (una impresora, un lector de
 * tarjetas…) y cada una varios "endpoints" (canales). Nos interesa la interfaz
 * de clase 7 y su endpoint de salida de tipo bulk, que es por donde se mandan
 * los datos a granel.
 */
function buscarSalidaUsb(dispositivo: USBDevice): { interfaz: number; endpoint: number } {
  const configuracion = dispositivo.configuration ?? dispositivo.configurations[0];
  if (!configuracion) {
    throw new ErrorDeImpresora("La impresora no respondió a la conexión.");
  }

  // Primero buscamos una interfaz declarada como impresora; si no hay ninguna
  // (algunas genéricas mienten y se declaran "vendor specific"), aceptamos
  // cualquiera que tenga una salida bulk.
  const candidatas = [
    ...configuracion.interfaces.filter((i) => i.alternate.interfaceClass === CLASE_USB_IMPRESORA),
    ...configuracion.interfaces,
  ];

  for (const interfaz of candidatas) {
    for (const endpoint of interfaz.alternate.endpoints) {
      if (endpoint.direction === "out" && endpoint.type === "bulk") {
        return { interfaz: interfaz.interfaceNumber, endpoint: endpoint.endpointNumber };
      }
    }
  }

  throw new ErrorDeImpresora(
    "Ese aparato no parece una impresora térmica.",
    "Revisa que hayas elegido la impresora y no otro dispositivo USB.",
  );
}

async function abrirUsb(dispositivo: USBDevice): Promise<CanalUsb> {
  try {
    if (!dispositivo.opened) await dispositivo.open();
    if (dispositivo.configuration === null) await dispositivo.selectConfiguration(1);

    const { interfaz, endpoint } = buscarSalidaUsb(dispositivo);
    await dispositivo.claimInterface(interfaz);

    return { tipo: "usb", dispositivo, interfaz, endpoint };
  } catch (e) {
    if (e instanceof ErrorDeImpresora) throw e;

    const texto = e instanceof Error ? e.message : String(e);

    /*
      El error más frecuente en Windows, y el que más tiempo hace perder si no
      se explica: el sistema tiene tomada la impresora y no la suelta.

      ⚠️ AQUÍ SE MANDABA A LA GENTE A ZADIG, Y SE QUITÓ.

      El consejo era cierto —Zadig cambia el driver a WinUSB y entonces sí
      imprime sin diálogo— pero lo que no se decía de frente es el precio: la
      impresora **desaparece de la lista de Windows** para todos los demás
      programas. Un bodeguero que siga ese consejo se queda sin poder imprimir
      desde ningún otro sitio y sin saber por qué, y deshacerlo cuesta media
      tarde.

      No hace falta arreglar nada: el botón Imprimir de siempre funciona, y
      desde que el recibo sale en PDF sale además del ancho del rollo. Lo único
      que se pierde es el corte automático del papel.
    */
    if (/claim|access|denied|busy|protected/i.test(texto)) {
      throw new ErrorDeImpresora(
        "Windows tiene reservada esa impresora y no la suelta.",
        "Es normal, y no hace falta arreglarlo: pasa en cuanto Windows reconoce la " +
          "impresora. Sigue usando el botón Imprimir de siempre — sale del ancho del " +
          "rollo igual. Lo único que se pierde es que corte el papel sola. Si quieres " +
          "eso, conéctala por Bluetooth o desde un Android: ahí sí funciona directo.",
      );
    }

    throw new ErrorDeImpresora("No se pudo abrir la impresora.", texto);
  }
}

// =============================================================================
//  Conexión Bluetooth
// =============================================================================

/**
 * Los "servicios" que publican las térmicas Bluetooth más comunes.
 *
 * No hay un estándar: cada fabricante eligió el suyo. Estos cuatro cubren la
 * enorme mayoría de las que se venden en Latinoamérica. Hay que declararlos por
 * adelantado porque el navegador, por seguridad, no deja hablar con un servicio
 * que no pediste en el diálogo de emparejar.
 */
const SERVICIOS_BLUETOOTH: (string | number)[] = [
  0x18f0, // el más común en las térmicas genéricas de 58 mm
  0xff00,
  0xffe0, // módulos HM-10, muy usados en impresoras baratas
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip / ISSC
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
];

/** Busca, entre todos los servicios de la impresora, uno en el que se pueda escribir. */
async function buscarEscrituraBluetooth(
  servidor: BluetoothRemoteGATTServer,
): Promise<BluetoothRemoteGATTCharacteristic> {
  let servicios: BluetoothRemoteGATTService[] = [];
  try {
    servicios = await servidor.getPrimaryServices();
  } catch {
    servicios = [];
  }

  for (const servicio of servicios) {
    let caracteristicas: BluetoothRemoteGATTCharacteristic[] = [];
    try {
      caracteristicas = await servicio.getCharacteristics();
    } catch {
      continue;
    }
    for (const c of caracteristicas) {
      if (c.properties.write || c.properties.writeWithoutResponse) return c;
    }
  }

  throw new ErrorDeImpresora(
    "Nos conectamos, pero esa impresora no acepta datos por Bluetooth.",
    "Puede que el aparato elegido no sea la impresora. Prueba a emparejarla de nuevo.",
  );
}

async function abrirBluetooth(dispositivo: BluetoothDevice): Promise<CanalBluetooth> {
  if (!dispositivo.gatt) {
    throw new ErrorDeImpresora("Esa impresora no permite conectarse por Bluetooth.");
  }

  const servidor = await dispositivo.gatt.connect();
  const caracteristica = await buscarEscrituraBluetooth(servidor);
  return { tipo: "bluetooth", dispositivo, caracteristica };
}

// =============================================================================
//  Emparejar (esto SÍ abre el diálogo del navegador)
// =============================================================================

/**
 * Pide al usuario que elija su impresora USB.
 *
 * Solo se puede llamar desde un clic real: el navegador bloquea el diálogo si
 * lo lanza código que el usuario no pidió. Por eso está detrás de un botón.
 */
/**
 * Solo mira QUÉ impresora es. No intenta abrirla.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y ESA DIFERENCIA ES TODO EL ASUNTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `emparejarUSB` pide el aparato y acto seguido lo ABRE. En Windows eso falla
 * en cuanto el sistema tiene la impresora reconocida —que es siempre— así que
 * la función revienta antes de guardar nada.
 *
 * Consecuencia: Softfi nunca llegaba a saber el modelo de una impresora en
 * Windows, justo en el único caso donde saberlo sirve para algo. La pantalla
 * de ayuda salía en blanco precisamente para quien la necesitaba.
 *
 * Pedir el aparato SÍ funciona aunque Windows lo tenga tomado: lo que el
 * sistema bloquea es hablarle, no verlo. Con eso basta para leer el código del
 * fabricante y decir qué driver hace falta.
 */
export async function identificarUSB(): Promise<{
  vendorId: number;
  productId: number;
  nombre?: string;
  /**
   * ¿Lo que eligió parece de verdad una impresora?
   *
   * ⚠️ HACE FALTA PORQUE EL CUADRO ENSEÑA TODO LO ENCHUFADO.
   *
   * Al quitar el filtro de clase —necesario, porque las térmicas baratas no se
   * declaran como impresoras— el cuadro del navegador pasa a listar también el
   * teclado, el ratón, la cámara y el pendrive. Con varias cosas conectadas es
   * fácil elegir la que no es, y entonces Softfi diría muy seguro «tu impresora
   * es un teclado Logitech» y mandaría a buscar un driver que no existe.
   *
   * No se puede impedir —hay impresoras que mienten sobre su clase— pero sí se
   * puede avisar.
   */
  pareceImpresora: boolean;
}> {
  if (!navigator.usb) {
    throw new ErrorDeImpresora(
      "Este navegador no puede ver impresoras USB.",
      "Funciona en Chrome, Edge y Opera de computadora, y en Chrome de Android. En iPhone no está disponible.",
    );
  }

  /*
    ⚠️ SIN FILTRO DE CLASE, a diferencia de `emparejarUSB`.

    Muchas térmicas baratas no se declaran como «clase impresora»: se presentan
    como aparato genérico o de proveedor. Con el filtro puesto, el cuadro del
    navegador sale VACÍO y la persona concluye que su impresora no sirve.

    Aquí no hay riesgo en enseñarlo todo: no se va a abrir nada, solo leer el
    código de lo que la persona elija. Y elige ella.
  */
  const dispositivo = await navigator.usb.requestDevice({ filters: [] });

  /*
    La clase 7 del estándar USB es «impresora». Se mira en el aparato entero y
    también interfaz por interfaz: muchas se declaran como «compuesto» por
    fuera y solo dicen que imprimen en una de sus interfaces.
  */
  const porInterfaz = (dispositivo.configuration?.interfaces ?? []).some((i) =>
    (i.alternates ?? []).some((a) => a.interfaceClass === CLASE_USB_IMPRESORA),
  );

  return {
    vendorId: dispositivo.vendorId,
    productId: dispositivo.productId,
    nombre: dispositivo.productName || dispositivo.manufacturerName || undefined,
    pareceImpresora: dispositivo.deviceClass === CLASE_USB_IMPRESORA || porInterfaz,
  };
}

export async function emparejarUSB(): Promise<ConfigImpresora> {
  if (!navigator.usb) {
    throw new ErrorDeImpresora(
      "Este navegador no puede hablar con impresoras USB.",
      "Funciona en Chrome, Edge y Opera de computadora, y en Chrome de Android. En iPhone no está disponible.",
    );
  }

  const dispositivo = await navigator.usb.requestDevice({
    filters: [{ classCode: CLASE_USB_IMPRESORA }],
  });

  const canal = await abrirUsb(dispositivo);
  await cerrarCanal();
  canalActivo = canal;

  const config: ConfigImpresora = {
    ...leerConfig(),
    conexion: "usb",
    vendorId: dispositivo.vendorId,
    productId: dispositivo.productId,
    bluetoothId: undefined,
    nombre: dispositivo.productName || dispositivo.manufacturerName || "Impresora USB",
  };
  guardarConfig(config);
  return config;
}

/** Lo mismo, para las impresoras Bluetooth. */
export async function emparejarBluetooth(): Promise<ConfigImpresora> {
  if (!navigator.bluetooth) {
    throw new ErrorDeImpresora(
      "Este navegador no puede hablar con impresoras Bluetooth.",
      "Funciona en Chrome de Android y en Chrome/Edge de computadora. En iPhone no está disponible.",
    );
  }

  // `acceptAllDevices` en vez de filtrar por servicio: muchas térmicas baratas
  // no anuncian su servicio hasta después de conectarse, así que filtrar las
  // dejaría fuera de la lista y el usuario no las vería nunca.
  const dispositivo = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: SERVICIOS_BLUETOOTH,
  });

  const canal = await abrirBluetooth(dispositivo);
  await cerrarCanal();
  canalActivo = canal;

  const config: ConfigImpresora = {
    ...leerConfig(),
    conexion: "bluetooth",
    bluetoothId: dispositivo.id,
    vendorId: undefined,
    productId: undefined,
    nombre: dispositivo.name || "Impresora Bluetooth",
  };
  guardarConfig(config);
  return config;
}

// =============================================================================
//  Reconectar en silencio (al abrir Softfi por la mañana)
// =============================================================================

/**
 * Intenta reconectar con la impresora que este aparato ya tenía, sin mostrar
 * ningún diálogo.
 *
 * Devuelve `true` si lo consiguió. Nunca lanza error: si no se puede, Softfi
 * sigue funcionando con la impresión normal y ya está. Que falle la impresora
 * jamás debe impedir vender.
 */
export async function reconectar(): Promise<boolean> {
  const config = leerConfig();
  if (config.conexion === null) return false;
  if (canalActivo !== null) return true;

  try {
    if (config.conexion === "usb" && navigator.usb) {
      const autorizados = await navigator.usb.getDevices();
      const dispositivo = autorizados.find(
        (d) => d.vendorId === config.vendorId && d.productId === config.productId,
      );
      if (!dispositivo) return false;
      canalActivo = await abrirUsb(dispositivo);
      return true;
    }

    if (config.conexion === "bluetooth" && navigator.bluetooth?.getDevices) {
      const autorizados = await navigator.bluetooth.getDevices();
      const dispositivo = autorizados.find((d) => d.id === config.bluetoothId);
      if (!dispositivo) return false;
      canalActivo = await abrirBluetooth(dispositivo);
      return true;
    }
  } catch {
    // Impresora apagada, desenchufada o fuera de alcance. No es un error que
    // haya que mostrarle a nadie: se reintenta la próxima vez que se imprima.
    canalActivo = null;
  }

  return false;
}

export function hayImpresoraConectada(): boolean {
  if (canalActivo === null) return false;
  if (canalActivo.tipo === "bluetooth") {
    // El Bluetooth se cae solo cuando la impresora se apaga o se aleja.
    return canalActivo.dispositivo.gatt?.connected ?? false;
  }
  return canalActivo.dispositivo.opened;
}

/** Cierra la conexión actual, sin borrar la configuración guardada. */
export async function cerrarCanal(): Promise<void> {
  const canal = canalActivo;
  canalActivo = null;
  if (!canal) return;

  try {
    if (canal.tipo === "usb") {
      await canal.dispositivo.releaseInterface(canal.interfaz);
      await canal.dispositivo.close();
    } else {
      canal.dispositivo.gatt?.disconnect();
    }
  } catch {
    /* Ya estaba cerrada o desenchufada: da igual. */
  }
}

/** Olvida la impresora de este dispositivo. */
export async function olvidarImpresora(): Promise<void> {
  await cerrarCanal();
  guardarConfig({ ...leerConfig(), conexion: null, nombre: undefined });
}

// =============================================================================
//  Enviar los bytes
// =============================================================================

/**
 * Por USB mandamos trozos de 4 KB. Un ticket entero de golpe hace que algunas
 * impresoras baratas se queden a medias, porque su memoria interna es pequeña.
 */
const TROZO_USB = 4096;

/**
 * Por Bluetooth los trozos son MUCHO más pequeños: el protocolo BLE manda
 * paquetes de 20 bytes por debajo, y además hay que darle tiempo a la impresora
 * a imprimir lo que ya recibió. Sin esta pausa, el ticket sale con líneas
 * repetidas o cortado a la mitad.
 */
const TROZO_BLUETOOTH = 180;
const PAUSA_BLUETOOTH_MS = 25;

function esperar(ms: number): Promise<void> {
  return new Promise((resolver) => setTimeout(resolver, ms));
}

/**
 * Manda un ticket ya construido a la impresora.
 *
 * Si la conexión se había caído (la impresora estaba apagada), intenta
 * reconectar una vez antes de darse por vencida.
 */
export async function imprimirBytes(bytes: Uint8Array): Promise<void> {
  if (!hayImpresoraConectada()) {
    canalActivo = null;
    const reconectado = await reconectar();
    if (!reconectado) {
      throw new ErrorDeImpresora(
        "No encuentro la impresora.",
        "Comprueba que esté encendida y con papel. Si está por Bluetooth, que no se haya alejado.",
      );
    }
  }

  const canal = canalActivo;
  if (!canal) {
    throw new ErrorDeImpresora("No encuentro la impresora.");
  }

  try {
    if (canal.tipo === "usb") {
      for (let i = 0; i < bytes.length; i += TROZO_USB) {
        await canal.dispositivo.transferOut(canal.endpoint, bytes.slice(i, i + TROZO_USB));
      }
      return;
    }

    const sinRespuesta = canal.caracteristica.properties.writeWithoutResponse;
    for (let i = 0; i < bytes.length; i += TROZO_BLUETOOTH) {
      const trozo = bytes.slice(i, i + TROZO_BLUETOOTH);
      if (sinRespuesta) {
        await canal.caracteristica.writeValueWithoutResponse(trozo);
        await esperar(PAUSA_BLUETOOTH_MS);
      } else {
        // Con confirmación la impresora nos marca el ritmo sola: no hace falta pausa.
        await canal.caracteristica.writeValue(trozo);
      }
    }
  } catch (e) {
    // Si falló a mitad, la conexión ya no sirve: la soltamos para que el próximo
    // intento la vuelva a abrir limpia.
    canalActivo = null;
    const texto = e instanceof Error ? e.message : String(e);
    throw new ErrorDeImpresora(
      "Se cortó la comunicación con la impresora.",
      texto.includes("papel") ? texto : "Revisa que tenga papel y siga encendida. " + texto,
    );
  }
}

/** Los nombres que mostramos en pantalla para cada tabla de caracteres. */
export const NOMBRES_DE_PAGINA: Record<PaginaDeCodigos, string> = {
  cp850: "CP850 — Latinoamérica (recomendada)",
  cp437: "CP437 — la más antigua",
  win1252: "Windows-1252",
};

/** Se exporta para que la pantalla de ajustes recorra las opciones sin repetir la lista. */
export const PAGINAS_DISPONIBLES = Object.keys(PAGINAS_DE_CODIGOS) as PaginaDeCodigos[];
